import * as React from "react";
import ActionButton, { ActionButtonType } from "../../Button/ActionButton";
import BaseLayout from "./BaseLayout";
import { HugeDialogFrame } from "./DialogFrames";
import { Fragment } from "components/Fragment";
import { isAllowed, PermissionCheckProps } from "components/PermissionCheck/PermissionCheck";
import BusyIndicator from "components/BusyIndicator/BusyIndicator";
import BusyFromPromise, {BusyState} from "components/BusyFromPromise/BusyFromPromise";
import ErrorPanel from "components/ErrorPanel/ErrorPanel";
import {Errors} from "components/DataBaseComponent/DataBaseComponent";
import DialogTitleBar from "./DialogTitleBar";
import { RenderProps } from "components/Dialog/CustomDialog";
import * as cn from "classnames";
import { defaultProps } from "recompose";

const styles = require("./SaveDialogLayout.less");

interface CustomSaveDialogLayoutProps extends RenderProps {
    errors: Errors;
    busy: BusyState;
    frame?: React.ComponentType<any>;
    renderTitle?: (props: ActionRenderProps) => React.ReactElement<any>;
    savePermission?: PermissionCheckProps;
    renderActions?: (props: ActionRenderProps) => React.ReactElement<any>;
    onSaveClick(): Promise<boolean>;
}

interface ActionRenderProps extends RenderProps {
    savePermission?: PermissionCheckProps;
    onSaveClick(): Promise<boolean>;
}

function isButtonDisabled(flag: boolean, savePermission?: PermissionCheckProps) {
    const disabledDueToPermission = !!savePermission ? !isAllowed(savePermission) : false;
    return !!(flag || disabledDueToPermission);
}

const continueIf = (predicate: () => Promise<boolean>, action: () => void) => {
    return () => predicate().then(x => {
        if (x) {
            action();
        }
        return x;
    });
};

interface DialogContentProps {
    className?: string;
}

const DialogContent: React.SFC<DialogContentProps> = ({className, children}) => {
    return <div className={cn(styles.content, className)}>
        {children}
    </div>;
};

export interface SaveDialogActionsProps {
    saveButtonLabel?: string;
    cancelButtonLabel?: string;
    saveDisabled?: boolean;
    savePermission?: PermissionCheckProps;
    onSaveClick(): Promise<boolean>;
    close(): void;
}

export const SaveDialogActions: React.SFC<SaveDialogActionsProps> = ({cancelButtonLabel = "Cancel", saveButtonLabel = "Save", saveDisabled = false, ...rest}) => {
    return <Fragment>
        <ActionButton label={cancelButtonLabel} onClick={rest.close} type={ActionButtonType.Secondary} />
        <ActionButton label={saveButtonLabel} onClick={continueIf(rest.onSaveClick, rest.close)} type={ActionButtonType.Save} disabled={isButtonDisabled(saveDisabled, rest.savePermission)} />
    </Fragment>;
};

export const SaveDialogTitleBar = defaultProps({ title: "Edit Details"})(DialogTitleBar);

const CustomSaveDialogLayout: React.SFC<CustomSaveDialogLayoutProps> = ({
        frame: Frame = HugeDialogFrame,
        busy,
        errors,
        renderTitle: RenderTitle = SaveDialogTitleBar,
        renderActions: RenderActions = SaveDialogActions,
        open,
        close,
        ...props
    }) => {
        return <BaseLayout
            open={open}
            close={close}
            render={(renderProps) =>
                <Frame>
                    {RenderTitle && <RenderTitle {...renderProps} onSaveClick={props.onSaveClick} savePermission={props.savePermission}/>}
                    {busy && <BusyFromPromise promise={busy}>
                        {(isBusy: boolean) => <BusyIndicator show={isBusy}/>}
                    </BusyFromPromise>}

                    {errors && <ErrorPanel message={errors.message} details={errors.details}/>}
                    <DialogContent>
                        {props.children}
                    </DialogContent>
                    {RenderActions && <RenderActions {...renderProps} onSaveClick={props.onSaveClick} savePermission={props.savePermission}/>}
                </Frame>
            }
        />;
};

export default CustomSaveDialogLayout;
export { CustomSaveDialogLayout, CustomSaveDialogLayoutProps };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/DialogLayout/Custom/SaveDialogLayout.tsx